import React, { useRef, useEffect } from 'react';

/* ═══════════════════════════════════════════════════════════════════
   GlobalCanvas
   ─────────────────────────────────────────────────────────────────
   A single <canvas> fixed behind all page content.
   As the user scrolls, scroll-percentage (0→1) drives which of the
   seven 3D scenes is rendered and how they cross-fade.

   Seven scenes (one per section):
     1. Hero           — perspective grid + drifting particle network
     2. About          — floating wireframe city blocks
     3. Services       — infinite 3D structural lattice
     4. Projects       — animated wireframe skyline
     5. Process        — rotating icosahedron / geodesic sphere
     6. Testimonials   — 3D neural-network mesh (teal accents)
     7. Contact/Footer — ground grid + pulsing rising columns
═══════════════════════════════════════════════════════════════════ */

/* ── Colour helpers — Muster Construct brand palette ──
   G = brand red (#E41E26)
   W = dark gray (#333333)
   T = darker red (#CC1B22) — network scene accent
*/
const G = (a) => `rgba(30,40,35,${a})`;     // brand red
const W = (a) => `rgba(51,51,51,${a})`;      // dark text/nodes
const T = (a) => `rgba(204,27,34,${a})`;     // darker red

/* ── 3D projection ──
   Rotates the point (x,y,z) around the X axis by ax,
   then around the Y axis by ay, then applies a simple
   isometric-style projection to 2D canvas space.
   @returns {{ px, py, z2 }} */
function proj(x, y, z, ax, ay, cx, cy, scale) {
  const cosX = Math.cos(ax), sinX = Math.sin(ax);
  const cosY = Math.cos(ay), sinY = Math.sin(ay);
  const y1 = y * cosX - z * sinX;
  const z1 = y * sinX + z * cosX;
  const x2 = x * cosY + z1 * sinY;
  const z2 = -x * sinY + z1 * cosY;
  return {
    px: cx + (x2 - y1 * 0.4) * scale,
    py: cy + (y1 * 0.6 + z2 * 0.3) * scale,
    z2,
  };
}

/* ─────────────────────────────────────────────────────────────────
   SCENE 1 — HERO: Perspective grid + drifting particle network
───────────────────────────────────────────────────────────────── */
function makeParticles(count = 80) {
  return Array.from({ length: count }, () => ({
    x: (Math.random() - 0.5) * 2000,
    y: (Math.random() - 0.5) * 1200,
    vx: (Math.random() - 0.5) * 0.35,
    vy: (Math.random() - 0.5) * 0.20,
    r: Math.random() * 1.4 + 0.3,
    a: Math.random() * 0.5 + 0.15,
    c: Math.random() > 0.6 ? 0 : 1,   // 0=gold, 1=white
  }));
}

function drawHeroScene(ctx, GW, GH, particles, t, alpha) {
  if (alpha <= 0) return;
  ctx.globalAlpha = alpha;

  const offsetY = GH * 0.1; /* Move the scene down by 10% of window height */

  /* perspective floor grid */
  const vpy = GH * 0.62 + offsetY, spread = GW * 0.9, depth = GH * 0.65;
  for (let i = 0; i <= 14; i++) {
    const p = i / 14, e = Math.pow(p, 0.55);
    ctx.beginPath();
    ctx.moveTo(GW * 0.5 - spread * e, vpy + depth * e);
    ctx.lineTo(GW * 0.5 + spread * e, vpy + depth * e);
    ctx.strokeStyle = G(p * 0.18); ctx.lineWidth = 0.6; ctx.stroke();
  }
  for (let i = 0; i <= 10; i++) {
    const xT = GW * 0.5 + (i / 10 - 0.5) * spread * 0.22;
    const xB = GW * 0.5 + (i / 10 - 0.5) * spread * 1.9;
    ctx.beginPath();
    ctx.moveTo(xT, vpy); ctx.lineTo(xB, vpy + depth);
    ctx.strokeStyle = G(0.06); ctx.lineWidth = 0.5; ctx.stroke();
  }

  /* particles */
  particles.forEach((p) => {
    p.x += p.vx; p.y += p.vy;
    if (p.x < -1000) p.x = 1000; if (p.x > 1000) p.x = -1000;
    if (p.y < -600) p.y = 600; if (p.y > 600) p.y = -600;
    const sx = GW * 0.5 + p.x * (GW / 2000);
    const sy = GH * 0.5 + offsetY + p.y * (GH / 1200);
    ctx.beginPath(); ctx.arc(sx, sy, p.r, 0, Math.PI * 2);
    ctx.fillStyle = p.c === 0 ? G(p.a) : W(p.a); ctx.fill();
  });

  /* connection lines */
  for (let i = 0; i < particles.length; i++) {
    for (let j = i + 1; j < particles.length; j++) {
      const pi = particles[i], pj = particles[j];
      const sx1 = GW * 0.5 + pi.x * (GW / 2000);
      const sy1 = GH * 0.5 + offsetY + pi.y * (GH / 1200);
      const sx2 = GW * 0.5 + pj.x * (GW / 2000);
      const sy2 = GH * 0.5 + offsetY + pj.y * (GH / 1200);
      const d = Math.hypot(sx1 - sx2, sy1 - sy2);
      if (d < 130) {
        ctx.beginPath(); ctx.moveTo(sx1, sy1); ctx.lineTo(sx2, sy2);
        ctx.strokeStyle = G((1 - d / 130) * 0.04);
        ctx.lineWidth = 0.35; ctx.stroke();
      }
    }
  }
  ctx.globalAlpha = 1;
}

/* ─────────────────────────────────────────────────────────────────
   SCENE 2 — ABOUT: Floating wireframe city blocks
───────────────────────────────────────────────────────────────── */
function makeCityBlocks(count = 18) {
  return Array.from({ length: count }, () => ({
    x: (Math.random() - 0.5) * 8,
    y: (Math.random() - 0.5) * 4,
    w: Math.random() * 0.8 + 0.4,
    d: Math.random() * 0.8 + 0.4,
    h: Math.random() * 3.0 + 0.5,
    phase: Math.random() * Math.PI * 2,
    speed: Math.random() * 0.4 + 0.2,
  }));
}

function drawCityScene(ctx, GW, GH, blocks, t, ax, ay, alpha) {
  if (alpha <= 0) return;
  ctx.globalAlpha = alpha;
  const cx = GW * 0.5, cy = GH * 0.52, sc = Math.min(GW, GH) * 0.045;
  const p3 = (x, y, z) => proj(x, y, z, ax, ay, cx, cy, sc);

  blocks.forEach((b, bi) => {
    const bz = Math.sin(t * 0.001 * b.speed + b.phase) * 0.3;

    /* three visible faces: front, right side, top */
    const faces = [
      { pts: [[b.x, b.y + b.d, bz], [b.x + b.w, b.y + b.d, bz], [b.x + b.w, b.y + b.d, bz + b.h], [b.x, b.y + b.d, bz + b.h]], fa: 0.12, st: G(0.35) },
      { pts: [[b.x + b.w, b.y, bz], [b.x + b.w, b.y + b.d, bz], [b.x + b.w, b.y + b.d, bz + b.h], [b.x + b.w, b.y, bz + b.h]], fa: 0.08, st: G(0.25) },
      { pts: [[b.x, b.y, bz + b.h], [b.x + b.w, b.y, bz + b.h], [b.x + b.w, b.y + b.d, bz + b.h], [b.x, b.y + b.d, bz + b.h]], fa: 0.18, st: G(0.50) },
    ];

    faces.forEach(({ pts, fa, st }) => {
      const ps = pts.map(([x, y, z]) => p3(x, y, z));
      ctx.beginPath(); ctx.moveTo(ps[0].px, ps[0].py);
      ps.slice(1).forEach((p) => ctx.lineTo(p.px, p.py));
      ctx.closePath();
      ctx.fillStyle = `rgba(240,240,240,${fa})`; ctx.fill();
      ctx.strokeStyle = st; ctx.lineWidth = 0.6; ctx.stroke();
    });

    /* animated window grid on front face */
    if (b.h > 1.2) {
      const rows = Math.floor(b.h / 0.4), cols = Math.max(1, Math.floor(b.w / 0.25));
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const lit = Math.sin(r * 2.1 + c * 1.7 + t * 0.0012 + bi) > 0.2;
          const wz1 = bz + 0.15 + r * 0.38;
          const wx1 = b.x + 0.05 + c * (b.w / cols);
          const wp = [
            p3(wx1, b.y + b.d + 0.01, wz1),
            p3(wx1 + 0.12, b.y + b.d + 0.01, wz1),
            p3(wx1 + 0.12, b.y + b.d + 0.01, wz1 + 0.22),
            p3(wx1, b.y + b.d + 0.01, wz1 + 0.22),
          ];
          ctx.beginPath(); ctx.moveTo(wp[0].px, wp[0].py);
          wp.slice(1).forEach((p) => ctx.lineTo(p.px, p.py));
          ctx.closePath();
          ctx.fillStyle = lit ? G(0.5) : W(0.06); ctx.fill();
        }
      }
    }
  });
  ctx.globalAlpha = 1;
}

/* ─────────────────────────────────────────────────────────────────
   SCENE 3 — SERVICES: Infinite 3D structural lattice
───────────────────────────────────────────────────────────────── */
function drawLatticeScene(ctx, GW, GH, t, ax, ay, alpha) {
  if (alpha <= 0) return;
  ctx.globalAlpha = alpha;
  const cx = GW * 0.5, cy = GH * 0.5, sc = Math.min(GW, GH) * 0.038;
  const N = 5;
  const p3 = (x, y, z) => proj(x, y, z, ax, ay, cx, cy, sc);

  for (let ix = -N; ix <= N; ix++) {
    for (let iy = -N; iy <= N; iy++) {
      for (let iz = -2; iz <= 2; iz++) {
        const pp = p3(ix, iy, iz);
        if (Math.abs(pp.z2) > 6) continue;

        ctx.beginPath(); ctx.arc(pp.px, pp.py, 0.8, 0, Math.PI * 2);
        ctx.fillStyle = G(0.25 - Math.abs(pp.z2) * 0.03); ctx.fill();

        /* beams to neighbours */
        if (ix < N) { const p2 = p3(ix + 1, iy, iz); ctx.beginPath(); ctx.moveTo(pp.px, pp.py); ctx.lineTo(p2.px, p2.py); ctx.strokeStyle = G(0.08); ctx.lineWidth = 0.4; ctx.stroke(); }
        if (iy < N) { const p2 = p3(ix, iy + 1, iz); ctx.beginPath(); ctx.moveTo(pp.px, pp.py); ctx.lineTo(p2.px, p2.py); ctx.strokeStyle = G(0.06); ctx.lineWidth = 0.4; ctx.stroke(); }
        if (iz < 2) { const p2 = p3(ix, iy, iz + 1); ctx.beginPath(); ctx.moveTo(pp.px, pp.py); ctx.lineTo(p2.px, p2.py); ctx.strokeStyle = G(0.12); ctx.lineWidth = 0.5; ctx.stroke(); }
      }
    }
  }
  ctx.globalAlpha = 1;
}

/* ─────────────────────────────────────────────────────────────────
   SCENE 4 — PROJECTS: Animated wireframe skyline
───────────────────────────────────────────────────────────────── */
const SKYLINE_BUILDINGS = [
  { x: -5.5, w: 1.5, h: 8, d: 1.2 },
  { x: -3.5, w: 2.0, h: 12, d: 1.5 },
  { x: -1.0, w: 1.2, h: 6, d: 1.0 },
  { x: 0.5, w: 2.5, h: 16, d: 2.0 },
  { x: 3.5, w: 1.5, h: 9, d: 1.2 },
  { x: 5.5, w: 1.0, h: 5, d: 1.0 },
  { x: -7.0, w: 1.0, h: 4, d: 0.8 },
  { x: 7.0, w: 1.2, h: 7, d: 1.0 },
];

function drawSkylineScene(ctx, GW, GH, t, ax, ay, alpha) {
  if (alpha <= 0) return;
  ctx.globalAlpha = alpha;
  const cx = GW * 0.5, cy = GH * 0.72, sc = Math.min(GW, GH) * 0.038;
  const p3 = (x, y, z) => proj(x, y, z, ax, ay, cx, cy, sc);

  SKYLINE_BUILDINGS.forEach((b, bi) => {
    const faces = [
      { corners: [[b.x, b.d, 0], [b.x + b.w, b.d, 0], [b.x + b.w, b.d, b.h], [b.x, b.d, b.h]], fa: 0.10, st: G(0.30) },
      { corners: [[b.x + b.w, 0, 0], [b.x + b.w, b.d, 0], [b.x + b.w, b.d, b.h], [b.x + b.w, 0, b.h]], fa: 0.07, st: G(0.20) },
      { corners: [[b.x, 0, b.h], [b.x + b.w, 0, b.h], [b.x + b.w, b.d, b.h], [b.x, b.d, b.h]], fa: 0.16, st: G(0.45) },
    ];

    faces.forEach(({ corners, fa, st }) => {
      const ps = corners.map(([x, y, z]) => p3(x, y, z));
      ctx.beginPath(); ctx.moveTo(ps[0].px, ps[0].py);
      ps.slice(1).forEach((p) => ctx.lineTo(p.px, p.py));
      ctx.closePath();
      ctx.fillStyle = `rgba(240,240,240,${fa})`; ctx.fill();
      ctx.strokeStyle = st; ctx.lineWidth = 0.7; ctx.stroke();
    });

    /* window grid on front face */
    const rows = Math.floor(b.h / 0.65), cols = Math.floor(b.w / 0.35);
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const lit = Math.sin(r * 2.3 + c * 1.8 + t * 0.0008 + bi * 1.1) > 0.15;
        const wz = 0.3 + r * 0.6, wx = b.x + 0.06 + c * (b.w / cols);
        const wp = [
          p3(wx, b.d + 0.01, wz),
          p3(wx + 0.2, b.d + 0.01, wz),
          p3(wx + 0.2, b.d + 0.01, wz + 0.35),
          p3(wx, b.d + 0.01, wz + 0.35),
        ];
        ctx.beginPath(); ctx.moveTo(wp[0].px, wp[0].py);
        wp.slice(1).forEach((p) => ctx.lineTo(p.px, p.py));
        ctx.closePath(); ctx.fillStyle = lit ? G(0.5) : W(0.04); ctx.fill();
      }
    }
  });
  ctx.globalAlpha = 1;
}

/* ─────────────────────────────────────────────────────────────────
   SCENE 5 — PROCESS: Rotating icosahedron / geodesic sphere
───────────────────────────────────────────────────────────────── */
const PHI = (1 + Math.sqrt(5)) / 2;
const ICO_VERTS = [
  [-1, PHI, 0], [1, PHI, 0], [-1, -PHI, 0], [1, -PHI, 0],
  [0, -1, PHI], [0, 1, PHI], [0, -1, -PHI], [0, 1, -PHI],
  [PHI, 0, -1], [PHI, 0, 1], [-PHI, 0, -1], [-PHI, 0, 1],
].map(([x, y, z]) => { const l = Math.hypot(x, y, z); return [x / l * 2.5, y / l * 2.5, z / l * 2.5]; });

const ICO_EDGES = (() => {
  const edges = [];
  for (let i = 0; i < ICO_VERTS.length; i++)
    for (let j = i + 1; j < ICO_VERTS.length; j++) {
      const [x1, y1, z1] = ICO_VERTS[i], [x2, y2, z2] = ICO_VERTS[j];
      if (Math.hypot(x1 - x2, y1 - y2, z1 - z2) < 2.6) edges.push([i, j]);
    }
  return edges;
})();

function drawGeodesicScene(ctx, GW, GH, t, ax, ay, alpha) {
  if (alpha <= 0) return;
  ctx.globalAlpha = alpha;
  const cx = GW * 0.5, cy = GH * 0.5, sc = Math.min(GW, GH) * 0.07;
  const p3 = (x, y, z) => proj(x, y, z, ax, ay, cx, cy, sc);

  /* outer latitudinal rings */
  for (let i = 0; i < 36; i++) {
    const a1 = (i / 36) * Math.PI * 2, a2 = ((i + 1) / 36) * Math.PI * 2;
    [0.5, 1, 1.5].forEach((rz) => {
      const p1 = p3(Math.cos(a1) * 3, Math.sin(a1) * 3, rz);
      const p2 = p3(Math.cos(a2) * 3, Math.sin(a2) * 3, rz);
      ctx.beginPath(); ctx.moveTo(p1.px, p1.py); ctx.lineTo(p2.px, p2.py);
      ctx.strokeStyle = G(0.04); ctx.lineWidth = 0.4; ctx.stroke();
    });
  }

  /* icosahedron edges */
  ICO_EDGES.forEach(([i, j]) => {
    const [x1, y1, z1] = ICO_VERTS[i], [x2, y2, z2] = ICO_VERTS[j];
    const pa = p3(x1, y1, z1), pb = p3(x2, y2, z2);
    ctx.beginPath(); ctx.moveTo(pa.px, pa.py); ctx.lineTo(pb.px, pb.py);
    ctx.strokeStyle = G(0.35); ctx.lineWidth = 0.8; ctx.stroke();
  });

  /* icosahedron vertices with pulse rings */
  ICO_VERTS.forEach(([x, y, z]) => {
    const pp = p3(x, y, z);
    ctx.beginPath(); ctx.arc(pp.px, pp.py, 2.5, 0, Math.PI * 2);
    ctx.fillStyle = G(0.7); ctx.fill();
    const pulse = (0.8 + 0.2 * Math.sin(t * 0.003 + x + y)) * 0.8;
    ctx.beginPath(); ctx.arc(pp.px, pp.py, 5 * pulse, 0, Math.PI * 2);
    ctx.strokeStyle = G(0.2); ctx.lineWidth = 0.5; ctx.stroke();
  });
  ctx.globalAlpha = 1;
}

/* ─────────────────────────────────────────────────────────────────
   SCENE 6 — TESTIMONIALS: 3D neural network mesh
───────────────────────────────────────────────────────────────── */
function makeNetNodes(count = 35) {
  return Array.from({ length: count }, () => ({
    x: (Math.random() - 0.5) * 14,
    y: (Math.random() - 0.5) * 8,
    z: (Math.random() - 0.5) * 6,
    phase: Math.random() * Math.PI * 2,
  }));
}

function drawNetworkScene(ctx, GW, GH, nodes, t, ax, ay, alpha) {
  if (alpha <= 0) return;
  ctx.globalAlpha = alpha;
  const cx = GW * 0.5, cy = GH * 0.5, sc = Math.min(GW, GH) * 0.04;
  const p3 = (x, y, z) => proj(x, y, z, ax, ay, cx, cy, sc);

  const pts = nodes.map((n) => {
    const pz = n.z + Math.sin(t * 0.0007 + n.phase) * 0.5;
    return p3(n.x, n.y, pz);
  });

  /* edges */
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const d = Math.hypot(nodes[i].x - nodes[j].x, nodes[i].y - nodes[j].y, nodes[i].z - nodes[j].z);
      if (d < 4.5) {
        ctx.beginPath(); ctx.moveTo(pts[i].px, pts[i].py); ctx.lineTo(pts[j].px, pts[j].py);
        ctx.strokeStyle = T((1 - d / 4.5) * 0.08); ctx.lineWidth = 0.4; ctx.stroke();
      }
    }
  }

  /* nodes */
  pts.forEach((p, i) => {
    const pulse = 0.8 + 0.2 * Math.sin(t * 0.002 + nodes[i].phase);
    ctx.beginPath(); ctx.arc(p.px, p.py, 2.5, 0, Math.PI * 2);
    ctx.fillStyle = T(0.5); ctx.fill();
    ctx.beginPath(); ctx.arc(p.px, p.py, 5 * pulse, 0, Math.PI * 2);
    ctx.strokeStyle = T(0.12); ctx.lineWidth = 0.5; ctx.stroke();
  });
  ctx.globalAlpha = 1;
}

/* ─────────────────────────────────────────────────────────────────
   SCENE 7 — CONTACT / FOOTER: Ground grid + rising columns
───────────────────────────────────────────────────────────────── */
const COLUMN_POSITIONS = [
  { x: -5, y: 0 }, { x: -2.5, y: 1 }, { x: 0, y: -1 }, { x: 2.5, y: 1 }, { x: 5, y: 0 },
  { x: -3.5, y: -2 }, { x: 3.5, y: -2 },
];

function drawColumnsScene(ctx, GW, GH, t, ax, ay, alpha) {
  if (alpha <= 0) return;
  ctx.globalAlpha = alpha;
  const cx = GW * 0.5, cy = GH * 0.68, sc = Math.min(GW, GH) * 0.042;
  const p3 = (x, y, z) => proj(x, y, z, ax, ay, cx, cy, sc);

  /* ground grid */
  for (let i = -8; i <= 8; i++) {
    let pa = p3(i, -5, 0), pb = p3(i, 5, 0);
    ctx.beginPath(); ctx.moveTo(pa.px, pa.py); ctx.lineTo(pb.px, pb.py);
    ctx.strokeStyle = G(0.06); ctx.lineWidth = 0.4; ctx.stroke();
    pa = p3(-8, i, 0); pb = p3(8, i, 0);
    ctx.beginPath(); ctx.moveTo(pa.px, pa.py); ctx.lineTo(pb.px, pb.py);
    ctx.strokeStyle = G(0.04); ctx.lineWidth = 0.4; ctx.stroke();
  }

  /* pulsing columns */
  COLUMN_POSITIONS.forEach((c, i) => {
    const h = 1.5 + 1.5 * Math.sin(t * 0.0012 + i * 0.8 + Math.PI / 3);
    const R = 0.2; /* half-width */
    const pts = [
      p3(c.x - R, c.y - R, 0), p3(c.x + R, c.y - R, 0),
      p3(c.x + R, c.y + R, 0), p3(c.x - R, c.y + R, 0),
      p3(c.x - R, c.y - R, h), p3(c.x + R, c.y - R, h),
      p3(c.x + R, c.y + R, h), p3(c.x - R, c.y + R, h),
    ];
    const drawQuad = (indices, fill, stroke) => {
      ctx.beginPath(); ctx.moveTo(pts[indices[0]].px, pts[indices[0]].py);
      indices.slice(1).forEach((k) => ctx.lineTo(pts[k].px, pts[k].py));
      ctx.closePath(); ctx.fillStyle = fill; ctx.fill();
      ctx.strokeStyle = stroke; ctx.lineWidth = 0.7; ctx.stroke();
    };
    drawQuad([0, 1, 5, 4], 'rgba(15,31,22,0.2)', G(0.35));
    drawQuad([1, 2, 6, 5], 'rgba(240,240,240,0.15)', G(0.25));
    drawQuad([4, 5, 6, 7], G(0.08), G(0.5));
    /* glowing top dot */
    ctx.beginPath(); ctx.arc(pts[4].px, pts[4].py, 2, 0, Math.PI * 2);
    ctx.fillStyle = G(0.8); ctx.fill();
  });
  ctx.globalAlpha = 1;
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════════ */

/* Section scroll boundaries as fractions (0–1) of page scroll range.
   Each entry: [start, end].
   There is intentional overlap for cross-fading between scenes. */
const BOUNDS = {
  hero: [0.00, 0.14],
  about: [0.10, 0.28],
  services: [0.24, 0.42],
  projects: [0.38, 0.56],
  process: [0.52, 0.70],
  testimonials: [0.65, 0.83],
  contact: [0.80, 1.00],
};

/* Returns 0–1 alpha for a scene given its [start,end] bounds. */
function sceneAlpha(scrollPct, start, end, fade = 0.06) {
  const p = scrollPct;
  if (p < start - fade || p > end + fade) return 0;
  if (p < start) return (p - (start - fade)) / fade;
  if (p > end) return 1 - (p - end) / fade;
  return 1;
}

export default function GlobalCanvas() {
  const canvasRef = useRef(null);
  const scrollRef = useRef(0);
  const mouseRef = useRef({ x: 0.5, y: 0.5 });
  const particles = useRef(makeParticles());
  const cityBlocks = useRef(makeCityBlocks());
  const netNodes = useRef(makeNetNodes());
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let GW, GH;

    const resize = () => {
      GW = canvas.width = window.innerWidth;
      GH = canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    /* track scroll percentage */
    const onScroll = () => {
      const maxY = document.body.scrollHeight - window.innerHeight;
      scrollRef.current = maxY > 0 ? Math.min(1, window.scrollY / maxY) : 0;
    };
    window.addEventListener('scroll', onScroll);

    /* mouse parallax */
    const onMouse = (e) => {
      mouseRef.current = { x: e.clientX / window.innerWidth, y: e.clientY / window.innerHeight };
    };
    window.addEventListener('mousemove', onMouse);

    const loop = (t) => {
      GW = canvas.width;
      GH = canvas.height;
      ctx.clearRect(0, 0, GW, GH);

      const sp = scrollRef.current;
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;

      /* slow base rotation driven by time + mouse parallax */
      const baseAX = t * 0.00015 + (my - 0.5) * 0.25;
      const baseAY = t * 0.00025 + (mx - 0.5) * 0.30;

      drawHeroScene(ctx, GW, GH, particles.current, t, sceneAlpha(sp, ...BOUNDS.hero));
      drawCityScene(ctx, GW, GH, cityBlocks.current, t, baseAX, baseAY + t * 0.0002, sceneAlpha(sp, ...BOUNDS.about));
      drawLatticeScene(ctx, GW, GH, t, baseAX * 0.8, baseAY + t * 0.00018, sceneAlpha(sp, ...BOUNDS.services));
      drawSkylineScene(ctx, GW, GH, t, baseAX * 0.6, baseAY + t * 0.00022, sceneAlpha(sp, ...BOUNDS.projects));
      drawGeodesicScene(ctx, GW, GH, t, baseAX + t * 0.00018, baseAY + t * 0.00014, sceneAlpha(sp, ...BOUNDS.process));
      drawNetworkScene(ctx, GW, GH, netNodes.current, t, baseAX, baseAY + t * 0.0002, sceneAlpha(sp, ...BOUNDS.testimonials));
      drawColumnsScene(ctx, GW, GH, t, baseAX * 0.7, baseAY + t * 0.00016, sceneAlpha(sp, ...BOUNDS.contact));

      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('mousemove', onMouse);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        display: 'block',
      }}
    />
  );
}
